import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/layout/Container';
import { Card } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { useMyAgentProfile } from '../../hooks/useAgent';
import { Store, CheckCircle, Clock, XCircle, MapPin, Mail, Phone, Globe } from 'lucide-react';
import { formatDate } from '../../utils/formatters';

const STATUS_CONFIG: Record<string, any> = {
  PENDING: { icon: Clock, color: 'warning' as const, label: 'Under Review', message: 'Your application is being reviewed by our team. We\'ll notify you once a decision is made.' },
  APPROVED: { icon: CheckCircle, color: 'success' as const, label: 'Approved', message: 'Your agent account is active. You can now sell tickets and access the agent dashboard.' },
  REJECTED: { icon: XCircle, color: 'danger' as const, label: 'Rejected', message: 'Your agent application has been rejected.' },
  DISABLED: { icon: XCircle, color: 'danger' as const, label: 'Disabled', message: 'Your agent account has been disabled. Please contact support for more information.' },
};

export const AgentStatus: React.FC = () => {
  const { data, isLoading } = useMyAgentProfile();

  if (isLoading) return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-12"><Spinner size="lg" /></Container>
    </div>
  );

  if (!data?.data) return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-12 max-w-lg text-center">
        <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">No Application Found</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">You haven't applied to become an agent yet.</p>
        <Link to="/agent/apply"><Button>Apply Now</Button></Link>
      </Container>
    </div>
  );

  const profile = data.data;
  const cfg = STATUS_CONFIG[profile.status];
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Agent Application</h1>

        <Card className="mb-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${profile.status === 'APPROVED' ? 'bg-emerald-100 dark:bg-emerald-900/50' : (profile.status === 'DISABLED' || profile.status === 'REJECTED') ? 'bg-red-100 dark:bg-red-900/50' : 'bg-amber-100 dark:bg-amber-900/50'}`}>
              <Icon className={`w-6 h-6 ${profile.status === 'APPROVED' ? 'text-emerald-600' : (profile.status === 'DISABLED' || profile.status === 'REJECTED') ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Status</h2>
                <Badge variant={cfg.color}>{cfg.label}</Badge>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{cfg.message}</p>
              {profile.approvedAt && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Approved on {formatDate(profile.approvedAt)}</p>}
              {profile.status === 'REJECTED' && profile.rejectionReason && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-800 rounded-xl">
                  <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Reason</p>
                  <p className="text-sm text-red-600 dark:text-red-300">{profile.rejectionReason}</p>
                </div>
              )}
              {profile.status === 'REJECTED' && (
                <Link to="/agent/apply" className="mt-3 inline-block">
                  <Button size="sm">Reapply</Button>
                </Link>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Application Details</h3>
          <div className="space-y-3">
            {profile.businessName && (
              <div className="flex items-start gap-3">
                <Store className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500 dark:text-gray-400">Business Name</p><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile.businessName}</p></div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Store Address</p><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile.storeAddress}</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Store className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Primary Activity</p><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile.storePrimaryActivity}</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Contact Email</p><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile.storeContactEmail}</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Contact Phone</p><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile.storeContactPhone}</p></div>
            </div>
            {profile.website && (
              <div className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500 dark:text-gray-400">Website</p><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile.website}</p></div>
              </div>
            )}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-400 dark:text-gray-500">Submitted on {formatDate(profile.createdAt)}</p>
            </div>
          </div>
        </Card>
      </Container>
    </div>
  );
};
